// ReferenceCenterModal.jsx - Combined modal (with Category Commission UI)
import React, { useState, useRef, useEffect } from "react";
import { X, MapPin } from "lucide-react";
import axios from "axios";
import API_BASE_URL from "../../../services/api";
import { Autocomplete } from "@react-google-maps/api";
import axiosInstance from "../../../utils/axiosInstance";

const ReferenceCenterModal = ({
  isOpen,
  onClose,
  onSave,
  center,
  mode = "create",
}) => {
  const [formData, setFormData] = useState({
    name: "",
    contactName: "",
    address: "",
    cityId: "",
    lat: null,
    long: null,
    email: "",
    alternativeEmail: "",
    mobile: "",
    isSelf: false,
  });

  const [loading, setLoading] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const autocompleteRef = useRef(null);

  // Cities
  const [cities, setCities] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [detectedCityName, setDetectedCityName] = useState("");

  // Categories
  const [allCategories, setAllCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

  // ✅ NEW: Commission map per category
  // { [categoryId]: { type: "PERCENT"|"AMOUNT", value: number, isActive: boolean } }
  const [commissionMap, setCommissionMap] = useState({});

  // ✅ maps loaded
  useEffect(() => {
    if (isOpen) setMapsLoaded(!!window.google?.maps?.places);
  }, [isOpen]);

  // ✅ Fetch cities + categories when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchCities = async () => {
      try {
        setCityLoading(true);
        const res = await axiosInstance.get(`${API_BASE_URL}/api/cities`, {
          withCredentials: true,
        });
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCities(list);
      } catch (e) {
        console.error("Failed to fetch cities:", e);
        setCities([]);
      } finally {
        setCityLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        setCatLoading(true);

        // ⚠️ keep your existing type filter if needed
        const res = await axiosInstance.get(
          `${API_BASE_URL}/api/categories/type?type=RADIOLOGY`,
          { withCredentials: true }
        );

        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.categories || [];

        setAllCategories(list);
      } catch (e) {
        console.error("Failed to fetch categories:", e);
        setAllCategories([]);
      } finally {
        setCatLoading(false);
      }
    };

    fetchCities();
    fetchCategories();
  }, [isOpen]);

  // ✅ init form on open (create/edit)
  useEffect(() => {
    if (!isOpen) return;

    if (center && mode === "edit") {
      setFormData({
        name: center.name || "",
        contactName: center.contactName || "",
        address: center.address || "",
        cityId: center.city?.id ? String(center.city.id) : "",
        lat: center.lat || null,
        long: center.long || null,
        email: center.email || "",
        alternativeEmail: center.alternativeEmail || "",
        mobile: center.mobile || "",
        isSelf: !!center.isSelf,
      });

      // Selected categories from center.categories
      if (Array.isArray(center.categories)) {
        const ids = center.categories
          .map((cc) => Number(cc.categoryId ?? cc.category?.id))
          .filter((n) => Number.isFinite(n) && n > 0);
        setSelectedCategoryIds(ids);
      } else {
        setSelectedCategoryIds([]);
      }

      // ✅ Load commissions in edit mode
      (async () => {
        try {
          const res = await axiosInstance.get(
            `${API_BASE_URL}/api/centers/${center.id}/commissions`
          );
          const list = res.data?.data || [];

          const nextMap = {};
          for (const row of list) {
            nextMap[row.categoryId] = {
              type: row.type || "PERCENT",
              value: Number(row.value || 0),
              isActive: row.isActive !== false,
            };
          }
          setCommissionMap(nextMap);
        } catch (e) {
          console.error("Failed to load commissions:", e);
          setCommissionMap({});
        }
      })();
    }

    if (mode === "create") {
      setFormData({
        name: "",
        contactName: "",
        address: "",
        cityId: "",
        lat: null,
        long: null,
        email: "",
        alternativeEmail: "",
        mobile: "",
        isSelf: false,
      });
      setSelectedCategoryIds([]);
      setCommissionMap({});
      setDetectedCityName("");
      setAutocomplete(null);
    }
  }, [isOpen, center, mode]);

  // ✅ Reset on close
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: "",
        contactName: "",
        address: "",
        cityId: "",
        lat: null,
        long: null,
        email: "",
        alternativeEmail: "",
        mobile: "",
        isSelf: false,
      });
      setAutocomplete(null);
      setDetectedCityName("");
      setSelectedCategoryIds([]);
      setCommissionMap({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  const onLoad = (autocompleteInstance) => setAutocomplete(autocompleteInstance);

  const setCityFromDetectedName = (cityName) => {
    if (!cityName) return;
    setDetectedCityName(cityName);

    const found = cities.find(
      (c) =>
        String(c.name).toLowerCase().trim() ===
        String(cityName).toLowerCase().trim()
    );

    if (found?.id) {
      setFormData((p) => ({ ...p, cityId: String(found.id) }));
    }
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (!(place && place.geometry && place.geometry.location)) return;

    const formattedAddress = place.formatted_address || "";
    let cityName = "";

    place.address_components?.forEach((component) => {
      const types = component.types;
      if (types.includes("locality")) cityName = component.long_name;
      else if (types.includes("administrative_area_level_2") && !cityName)
        cityName = component.long_name;
      else if (types.includes("administrative_area_level_1") && !cityName)
        cityName = component.long_name;
    });

    setFormData((p) => ({
      ...p,
      address: formattedAddress,
      lat: place.geometry.location.lat(),
      long: place.geometry.location.lng(),
    }));

    if (autocompleteRef.current) {
      autocompleteRef.current.value = formattedAddress;
    }

    setCityFromDetectedName(cityName || place.vicinity || "");
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    handleChange("address", value);

    setFormData((p) => ({
      ...p,
      lat: null,
      long: null,
      cityId: "",
    }));
    setDetectedCityName("");
  };

  const geocodeAddress = async () => {
    if (!formData.address || !mapsLoaded) return false;

    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: formData.address }, (results, status) => {
          if (status === "OK" && results[0]) resolve(results[0]);
          else reject(new Error("Geocoding failed"));
        });
      });

      let cityName = "";
      result.address_components?.forEach((component) => {
        const types = component.types;
        if (types.includes("locality")) cityName = component.long_name;
        else if (types.includes("administrative_area_level_2") && !cityName)
          cityName = component.long_name;
      });

      setFormData((p) => ({
        ...p,
        lat: result.geometry.location.lat(),
        long: result.geometry.location.lng(),
      }));

      setCityFromDetectedName(cityName);
      return true;
    } catch (err) {
      console.error("Geocoding error:", err);
      return false;
    }
  };

  // ✅ Commission helpers
  const ensureCommissionRow = (categoryId) => {
    setCommissionMap((prev) => {
      if (prev[categoryId]) return prev;
      return {
        ...prev,
        [categoryId]: { type: "PERCENT", value: 0, isActive: true },
      };
    });
  };

  const updateCommission = (categoryId, patch) => {
    setCommissionMap((prev) => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || { type: "PERCENT", value: 0, isActive: true }),
        ...patch,
      },
    }));
  };

  const toggleCategory = (id) => {
    setSelectedCategoryIds((prev) => {
      const exists = prev.includes(id);
      if (exists) return prev.filter((x) => x !== id);

      ensureCommissionRow(id);
      return [...prev, id];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.name.trim()) return alert("Center name is required");
      if (!formData.address.trim()) return alert("Address is required");
      if (!formData.cityId) return alert("Please select a City");

      if ((!formData.lat || !formData.long) && mapsLoaded) {
        const ok = await geocodeAddress();
        if (!ok) return alert("Could not validate address. Use address search.");
      }

      const payload = {
        name: formData.name,
        contactName: formData.contactName || null,
        address: formData.address || null,
        cityId: Number(formData.cityId),
        email: formData.email || null,
        alternativeEmail: formData.alternativeEmail || null,
        mobile: formData.mobile || null,
        lat: formData.lat ? Number(formData.lat) : null,
        long: formData.long ? Number(formData.long) : null,
        status: "active",
        isSelf: !!formData.isSelf,
      };

      let response;

      if (mode === "edit" && center?.id) {
        response = await axiosInstance.put(
          `${API_BASE_URL}/api/centers/${center.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        response = await axiosInstance.post(
          `${API_BASE_URL}/api/centers`,
          payload,
          { withCredentials: true }
        );
      }

      const savedCenter = response.data.center || response.data;

      // ✅ Save categories (you already do this)
      await axiosInstance.post(
        `${API_BASE_URL}/api/centers/${savedCenter.id}/categories`,
        { categoryIds: selectedCategoryIds },
        { withCredentials: true }
      );

      // ✅ Save commissions for selected categories
      const commissionsPayload = selectedCategoryIds.map((categoryId) => {
        const row = commissionMap[categoryId] || {
          type: "PERCENT",
          value: 0,
          isActive: true,
        };

        return {
          categoryId,
          type: row.type || "PERCENT",
          value: Number(row.value || 0),
          isActive: row.isActive !== false,
        };
      });

      await axiosInstance.put(
        `${API_BASE_URL}/api/centers/${savedCenter.id}/commissions`,
        { commissions: commissionsPayload },
        { withCredentials: true }
      );

      // ✅ Fetch fresh center details
      const fresh = await axiosInstance.get(
        `${API_BASE_URL}/api/centers/${savedCenter.id}`,
        { withCredentials: true }
      );

      onSave?.(fresh.data);
      onClose();
    } catch (error) {
      console.error(`${mode === "edit" ? "Update" : "Creation"} failed:`, error);
      alert(
        error.response?.data?.error ||
          `Failed to ${mode === "edit" ? "update" : "save"} center`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {mode === "edit" ? "Edit Center" : "New Center"}
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Center Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                {/* Contact name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => handleChange("contactName", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Is Self Checkbox */}
                <div className="flex items-center gap-2">
                  <input
                    id="isSelf"
                    type="checkbox"
                    checked={!!formData.isSelf}
                    onChange={(e) => handleChange("isSelf", e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label htmlFor="isSelf" className="text-xs text-gray-700">
                    This is a Novus-owned center (Self)
                  </label>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Address <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-gray-500">
                      (Start typing to search)
                    </span>
                  </label>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className="h-4 w-4 text-gray-400" />
                    </div>

                    {mapsLoaded ? (
                      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                        <input
                          ref={autocompleteRef}
                          type="text"
                          value={formData.address}
                          onChange={handleAddressChange}
                          placeholder="Search address or enter manually"
                          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </Autocomplete>
                    ) : (
                      <input
                        ref={autocompleteRef}
                        type="text"
                        value={formData.address}
                        onChange={handleAddressChange}
                        placeholder="Enter address"
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    )}

                    {mapsLoaded && formData.address && (
                      <button
                        type="button"
                        onClick={geocodeAddress}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-blue-600 hover:text-blue-800"
                        title="Geocode address"
                      >
                        <span className="text-xs font-medium">Locate</span>
                      </button>
                    )}
                  </div>

                  {(detectedCityName || formData.lat || formData.long) && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                      <div className="grid grid-cols-2 gap-2">
                        {detectedCityName && (
                          <div className="col-span-2">
                            <span className="font-medium text-blue-800">
                              Detected City:
                            </span>
                            <span className="ml-2 text-blue-700">
                              {detectedCityName}
                            </span>
                          </div>
                        )}
                        {formData.lat && formData.long && (
                          <>
                            <div>
                              <span className="font-medium text-blue-800">
                                Latitude:
                              </span>
                              <span className="ml-2 text-blue-700">
                                {Number(formData.lat).toFixed(6)}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-blue-800">
                                Longitude:
                              </span>
                              <span className="ml-2 text-blue-700">
                                {Number(formData.long).toFixed(6)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* City dropdown */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.cityId}
                    onChange={(e) => handleChange("cityId", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={cityLoading}
                  >
                    <option value="">
                      {cityLoading ? "Loading..." : "Select City"}
                    </option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>

                  {detectedCityName && !formData.cityId && (
                    <p className="text-xs text-yellow-700 mt-1">
                      Detected city "{detectedCityName}" not in DB. Select manually.
                    </p>
                  )}
                </div>

                {/* ✅ Categories + Commission */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Categories + Commission
                    </label>
                    <span className="text-xs text-gray-500">
                      {selectedCategoryIds.length} selected
                    </span>
                  </div>

                  {catLoading ? (
                    <p className="text-xs text-gray-500">Loading categories...</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {allCategories.map((cat) => {
                          const selected = selectedCategoryIds.includes(cat.id);
                          return (
                            <button
                              type="button"
                              key={cat.id}
                              onClick={() => toggleCategory(cat.id)}
                              className={`flex items-center justify-between text-xs px-3 py-2 rounded border ${
                                selected
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-gray-700 border-gray-200"
                              }`}
                            >
                              <span className="truncate">{cat.name}</span>
                              <input type="checkbox" readOnly checked={selected} />
                            </button>
                          );
                        })}
                      </div>

                      {selectedCategoryIds.length > 0 && (
                        <div className="border border-gray-200 rounded p-3 bg-gray-50">
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            Commission per selected category
                          </div>

                          <div className="space-y-2">
                            {selectedCategoryIds.map((catId) => {
                              const cat = allCategories.find((x) => x.id === catId);
                              const row =
                                commissionMap[catId] || {
                                  type: "PERCENT",
                                  value: 0,
                                  isActive: true,
                                };

                              return (
                                <div
                                  key={catId}
                                  className="grid grid-cols-12 gap-2 items-center bg-white border border-gray-200 rounded p-2"
                                >
                                  <div className="col-span-12 sm:col-span-5">
                                    <div className="text-xs font-medium text-gray-900 truncate">
                                      {cat?.name || `Category #${catId}`}
                                    </div>
                                  </div>

                                  <div className="col-span-6 sm:col-span-3">
                                    <select
                                      value={row.type}
                                      onChange={(e) =>
                                        updateCommission(catId, { type: e.target.value })
                                      }
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                    >
                                      <option value="PERCENT">Percent (%)</option>
                                      <option value="AMOUNT">Amount (₹)</option>
                                    </select>
                                  </div>

                                  <div className="col-span-4 sm:col-span-3">
                                    <input
                                      type="number"
                                      min="0"
                                      max={row.type === "PERCENT" ? 100 : undefined}
                                      value={row.value}
                                      onChange={(e) =>
                                        updateCommission(catId, {
                                          value: Number(e.target.value || 0),
                                        })
                                      }
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                      placeholder={row.type === "PERCENT" ? "0-100" : "₹"}
                                    />
                                  </div>

                                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                                    <input
                                      type="checkbox"
                                      checked={row.isActive !== false}
                                      onChange={(e) =>
                                        updateCommission(catId, {
                                          isActive: e.target.checked,
                                        })
                                      }
                                      title="Active"
                                      className="h-4 w-4"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Email + Alt */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Alternative Email
                    </label>
                    <input
                      type="email"
                      value={formData.alternativeEmail}
                      onChange={(e) => handleChange("alternativeEmail", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Mobile
                  </label>
                  <div className="flex">
                    <div className="flex items-center px-2 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l text-xs text-gray-600 font-medium">
                      +91
                    </div>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) handleChange("mobile", value);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      maxLength="10"
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mr-2"></span>
                      {mode === "edit" ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    `${mode === "edit" ? "Update" : "Save"} Center`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferenceCenterModal;
